import { useState } from "react";
import { Plus, Pencil, Trash2, Wrench } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, FormError, SubmitButton, LoadingState, ErrorState, EmptyState, StatusBadge, ConfirmDialog } from "@/components/common";
import { useGarage } from "@/context/GarageContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { t } from "@/i18n";

const EMPTY = { name: "", description: "", duration_min: 60, buffer_min: 10, price_type: "fixed", price: "", active: true, required_skill: "", mechanic_user_id: "", resource_id: "", confirmation_mode: "auto", slot_type: "work", online_bookable: true, parts_confirmation: false, service_kind: "service", customer_parts_required: false };
const NONE = "__none";

function ServiceDialog({ service, open, onOpenChange, onSaved, booking }) {
  const [f, setF] = useState(EMPTY);
  const [seen, setSeen] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (open && seen !== service) {
    setSeen(service);
    setF(service ? { ...EMPTY, ...service, price: service.price ?? "", required_skill: service.required_skill || "", mechanic_user_id: service.mechanic_user_id || "", resource_id: service.resource_id || "" } : EMPTY);
    setError("");
  }
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const payload = { name: f.name, description: f.description, duration_min: Number(f.duration_min), buffer_min: Number(f.buffer_min),
      price_type: f.price_type, price: f.price === "" || f.price_type === "on_quote" ? null : Number(f.price), active: f.active,
      required_skill: f.required_skill || null, mechanic_user_id: f.mechanic_user_id || null, resource_id: f.resource_id || null,
      confirmation_mode: f.confirmation_mode, slot_type: f.slot_type, online_bookable: f.online_bookable, parts_confirmation: !!f.parts_confirmation,
      service_kind: f.service_kind || "service", customer_parts_required: f.service_kind === "diagnostic" ? false : !!f.customer_parts_required };
    try {
      if (service) await api.put(`/garage/services/${service.id}`, payload);
      else await api.post("/garage/services", payload);
      toast.success(t("common.saved"));
      onSaved();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setSeen(undefined); }}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="service-dialog">
        <DialogHeader>
          <DialogTitle>{service ? t("services.edit") : t("services.add")}</DialogTitle>
          <DialogDescription>{t("services.phaseNote")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <FormError message={error} />
          <Field label={t("services.name")} htmlFor="sv-name"><Input id="sv-name" required value={f.name} onChange={set("name")} data-testid="service-name-input" /></Field>
          <Field label={t("services.description")} htmlFor="sv-desc"><Textarea id="sv-desc" value={f.description} onChange={set("description")} data-testid="service-description-input" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("services.duration")} htmlFor="sv-dur"><Input id="sv-dur" type="number" min={5} required value={f.duration_min} onChange={set("duration_min")} data-testid="service-duration-input" /></Field>
            <Field label={t("services.buffer")} htmlFor="sv-buf"><Input id="sv-buf" type="number" min={0} required value={f.buffer_min} onChange={set("buffer_min")} data-testid="service-buffer-input" /></Field>
            <Field label={t("services.priceType")}>
              <Select value={f.price_type} onValueChange={(v) => setF({ ...f, price_type: v })}>
                <SelectTrigger data-testid="service-price-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["fixed", "indicative", "on_quote"].map((p) => <SelectItem key={p} value={p}>{t(`status.${p}`)}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {f.price_type !== "on_quote" && (
              <Field label={t("services.price")} htmlFor="sv-price"><Input id="sv-price" type="number" min={0} step="0.01" required value={f.price} onChange={set("price")} data-testid="service-price-input" /></Field>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("svc2.confirmation")}>
              <Select value={f.confirmation_mode} onValueChange={(v) => setF({ ...f, confirmation_mode: v })}>
                <SelectTrigger data-testid="service-confirmation-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["auto", "validation"].map((p) => <SelectItem key={p} value={p}>{t(`svc2.mode.${p}`)}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t("svc2.slotType")}>
              <Select value={f.slot_type} onValueChange={(v) => setF({ ...f, slot_type: v })}>
                <SelectTrigger data-testid="service-slot-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["work", "dropoff"].map((p) => <SelectItem key={p} value={p}>{t(`status.${p}`)}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t("svc2.skill")} htmlFor="sv-skill" hint={t("svc2.skillHint")}><Input id="sv-skill" value={f.required_skill} onChange={set("required_skill")} data-testid="service-skill-input" /></Field>
            <Field label={t("svc2.mechanic")}>
              <Select value={f.mechanic_user_id || NONE} onValueChange={(v) => setF({ ...f, mechanic_user_id: v === NONE ? "" : v })}>
                <SelectTrigger data-testid="service-mechanic-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>{t("svc2.anyone")}</SelectItem>{(booking?.staff || []).map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t("svc2.resource")} className="col-span-2">
              <Select value={f.resource_id || NONE} onValueChange={(v) => setF({ ...f, resource_id: v === NONE ? "" : v })}>
                <SelectTrigger data-testid="service-resource-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>{t("svc2.noResource")}</SelectItem>{(booking?.resources || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.name} (×{r.count})</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <label className="flex items-center justify-between text-sm">
            <span>{t("svc2.onlineBookable")}</span>
            <Switch checked={f.online_bookable} onCheckedChange={(v) => setF({ ...f, online_bookable: v })} data-testid="service-online-switch" />
          </label>
          <Field label={t("b10.cparts.kind")}>
            <Select value={f.service_kind || "service"} onValueChange={(v) => setF({ ...f, service_kind: v, customer_parts_required: v === "diagnostic" ? false : f.customer_parts_required })}>
              <SelectTrigger data-testid="service-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["service", "diagnostic"].map((k) => <SelectItem key={k} value={k}>{t(`b10.cparts.kinds.${k}`)}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <label className="flex items-start justify-between gap-3 text-sm">
            <span>{t("b10.cparts.switch")}<span className="block text-xs text-slate-500">{f.service_kind === "diagnostic" ? t("b10.cparts.naDiag") : t("b10.cparts.hint")}</span></span>
            <Switch checked={f.service_kind !== "diagnostic" && !!f.customer_parts_required} disabled={f.service_kind === "diagnostic"} onCheckedChange={(v) => setF({ ...f, customer_parts_required: v })} data-testid="service-customer-parts-switch" />
          </label>
          <label className="flex items-start justify-between gap-3 text-sm">
            <span>{t("b9.parts.serviceSwitch")}<span className="block text-xs text-slate-500">{t("b9.parts.serviceHint")}</span></span>
            <Switch checked={!!f.parts_confirmation} onCheckedChange={(v) => setF({ ...f, parts_confirmation: v })} data-testid="service-parts-switch" />
          </label>
          <label className="flex items-center justify-between text-sm">
            <span>{t("services.visible")}</span>
            <Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="service-active-switch" />
          </label>
          <div className="gf-modal-footer flex gap-2" data-testid="service-dialog-footer">
            <Button type="button" variant="outline" className="flex-1" onClick={() => onOpenChange(false)} data-testid="service-cancel-button">{t("common.cancel")}</Button>
            <SubmitButton busy={busy} className="flex-1" data-testid="service-save-button">{t("common.save")}</SubmitButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ServicesManager() {
  const { garage, canManage } = useGarage();
  const { data, error, loading, reload } = useFetch("/garage/services");
  const booking = useFetch("/booking/settings");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [del, setDel] = useState(null);

  const remove = async () => {
    try {
      await api.delete(`/garage/services/${del.id}`);
      toast.success(t("common.deleted"));
      setDel(null);
      reload();
    } catch (e) {
      if (e?.response?.status === 409) {
        await api.put(`/garage/services/${del.id}`, { ...del, active: false, online_bookable: false });
        toast.message(t("services.archivedInstead"));
        setDel(null);
        reload();
      } else toast.error(errMsg(e));
    }
  };

  const addBtn = canManage && <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-service-button"><Plus className="mr-2 h-4 w-4" /> {t("services.add")}</Button>;
  if (loading && !data) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div data-testid="services-manager">
      {data.length === 0 ? (
        <EmptyState icon={Wrench} title={t("services.empty")} text={t("services.emptyText")} action={addBtn} />
      ) : (
        <>
          <div className="mb-4 flex justify-end">{addBtn}</div>
          <div className="space-y-2">
            {data.map((s) => (
              <div key={s.id} className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 sm:flex-row sm:flex-wrap sm:items-center" data-testid={`service-row-${s.id}`}>
                <div className="min-w-0 sm:flex-1">
                  <p className="font-semibold text-slate-900">{s.name} {!s.active && <span className="text-xs font-normal text-slate-500">({t("services.hidden")})</span>}</p>
                  <p className="text-xs text-slate-500">{t("services.durationLine", { d: s.duration_min, b: s.buffer_min })}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                <StatusBadge status={s.slot_type || "work"} />
                <StatusBadge status={s.confirmation_mode || "auto"} />
                {s.parts_confirmation && <StatusBadge status="awaiting_parts" testId={`service-parts-badge-${s.id}`} />}
                <StatusBadge status={s.price_type} />
                </div>
                <div className="flex items-center justify-between gap-2 sm:justify-end">
                <span className="text-sm font-semibold sm:w-24 sm:text-right">{s.price_type === "on_quote" ? "—" : fmtMoney(s.price, garage.currency)}</span>
                {canManage && (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => { setEditing(s); setOpen(true); }} data-testid={`edit-service-${s.id}`} aria-label={t("common.edit")}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="text-red-600" onClick={() => setDel(s)} data-testid={`delete-service-${s.id}`} aria-label={t("common.delete")}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      <ServiceDialog booking={booking.data} service={editing} open={open} onOpenChange={setOpen} onSaved={() => { setOpen(false); reload(); }} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} destructive title={t("services.deleteTitle")} description={t("services.deleteText", { name: del?.name })} confirmLabel={t("common.delete")} onConfirm={remove} testId="delete-service-dialog" />
    </div>
  );
}
